import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import { TutorQuizDTO } from '../../services/tutorQuizService';
import { LiveQuizSessionDTO, dispatchLiveQuiz, closeLiveQuiz } from '../../services/tutorSessionService';

interface Props {
  courseId: number;
  topicNodeId: number | null;
  quiz: TutorQuizDTO | null;
  activeQuizSession: LiveQuizSessionDTO | null;
  onQuizDispatched: (session: LiveQuizSessionDTO) => void;
  onQuizClosed: () => void;
}

export default function QuizPanel({
  courseId,
  topicNodeId,
  quiz,
  activeQuizSession,
  onQuizDispatched,
  onQuizClosed,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [timerSeconds, setTimerSeconds] = useState(60);
  const [error, setError] = useState<string | null>(null);
  const [dispatching, setDispatching] = useState(false);
  const [closing, setClosing] = useState(false);

  const questions = quiz?.questions ?? [];

  function toggleQuestion(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleDispatch() {
    if (selectedIds.size === 0) {
      setError('Select at least one question.');
      return;
    }
    setError(null);
    setDispatching(true);
    try {
      const session = await dispatchLiveQuiz(courseId, topicNodeId, Array.from(selectedIds), timerSeconds);
      onQuizDispatched(session);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError('A quiz is already active. Close it before dispatching another.');
      } else {
        setError('Failed to dispatch quiz. Please try again.');
      }
    } finally {
      setDispatching(false);
    }
  }

  async function handleClose() {
    setClosing(true);
    setError(null);
    try {
      await closeLiveQuiz(courseId);
      onQuizClosed();
    } catch {
      setError('Failed to close quiz.');
    } finally {
      setClosing(false);
    }
  }

  if (activeQuizSession) {
    const elapsed = Math.floor((Date.now() - new Date(activeQuizSession.startedAt).getTime()) / 1000);
    const remaining = Math.max(0, activeQuizSession.timerSeconds - elapsed);
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <QuizIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>Quiz Active</Typography>
        </Stack>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {activeQuizSession.questionIds.length} question{activeQuizSession.questionIds.length !== 1 ? 's' : ''} &bull; Timer: {remaining}s remaining
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleClose}
          disabled={closing}
        >
          {closing ? 'Closing…' : 'Close Quiz'}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <QuizIcon color="action" />
        <Typography variant="subtitle1" fontWeight={600}>Quiz</Typography>
      </Stack>

      {questions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No questions saved for this topic. Add questions from the course builder.
        </Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Select questions to ask students:
          </Typography>
          <Stack spacing={0.5} sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
            {questions.map(q => (
              <FormControlLabel
                key={q.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(q.id)}
                    onChange={() => toggleQuestion(q.id)}
                  />
                }
                label={<Typography variant="body2">{q.questionText}</Typography>}
              />
            ))}
          </Stack>

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <TextField
              label="Timer (seconds)"
              type="number"
              size="small"
              value={timerSeconds}
              onChange={e => setTimerSeconds(Math.max(10, parseInt(e.target.value) || 60))}
              inputProps={{ min: 10 }}
              sx={{ width: 140 }}
            />
            <Typography variant="caption" color="text.secondary">
              {selectedIds.size} selected
            </Typography>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

          <Button
            variant="contained"
            size="small"
            startIcon={<QuizIcon />}
            onClick={handleDispatch}
            disabled={dispatching || selectedIds.size === 0}
          >
            {dispatching ? 'Dispatching…' : 'Ask Students'}
          </Button>
        </>
      )}
    </Box>
  );
}
